import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import PublicLanding from './components/PublicLanding';
import PaymentSuccess from './components/PaymentSuccess';
import PrivacyPolicy from './components/PrivacyPolicy';
import PublicOffer from './components/PublicOffer';
import Contacts from './components/Contacts';
import Courses from './components/Courses';
import CourseForm from './components/CourseForm';
import CookiesModal from './components/CookiesModal';
import BlogList from './components/BlogList';
import BlogPost from './components/BlogPost';
import GoogleAnalytics from './components/GoogleAnalytics';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  
  // Обработка токена из URL параметра (для OAuth callback)
  const { fetchUser } = useAuth();
  
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      // Вызываем специальный endpoint для установки httpOnly cookie
      const currentPath = window.location.pathname;
      axios.get(`/api/auth/set-token?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(currentPath)}`, {
        withCredentials: true
      }).then(() => {
        // Удаляем token из URL без перезагрузки страницы
        params.delete('token');
        const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', newUrl);
        
        // Обновляем состояние пользователя без перезагрузки страницы
        // Небольшая задержка, чтобы cookie успела установиться
        setTimeout(() => {
          fetchUser();
        }, 100);
      }).catch((error) => {
        console.error('Error setting token:', error);
        // В случае ошибки редиректим на login
        window.location.href = '/login?error=token_set_failed';
      });
    }
  }, [fetchUser]);
  
  // Редирект авторизованных пользователей с корневого пути на /home
  React.useEffect(() => {
    if (!loading && user && window.location.pathname === '/') {
      window.location.href = '/home';
    }
  }, [user, loading]);
  
  return (
    <Routes>
      <Route 
        path="/login" 
        element={user ? <Navigate to="/home" replace /> : <Login />} 
      />
      <Route 
        path="/register" 
        element={user ? <Navigate to="/home" replace /> : <Register />} 
      />
      <Route 
        path="/home" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/models" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/models/:modelId" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/topup" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payments" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/effects" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/referral" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/fail" 
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/" 
        element={
          loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : user ? (
            <Navigate to="/home" replace />
          ) : (
            <PublicLanding />
          )
        } 
      />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/public-offer" element={<PublicOffer />} />
      <Route path="/contacts" element={<Contacts />} />
      {/* Курсы — публичные страницы, без авторизации */}
      <Route path="/courses" element={<Courses />} />
      <Route path="/courses/form" element={<CourseForm />} />
      {/* Блог доступен публично для краулеров соцсетей */}
      {/* Для авторизованных - через Dashboard, для неавторизованных - публичная версия */}
      <Route 
        path="/blog" 
        element={
          user ? (
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          ) : (
            <BlogList />
          )
        } 
      />
      <Route 
        path="/blog/:slug" 
        element={
          user ? (
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          ) : (
            <BlogPost />
          )
        } 
      />
      <Route 
        path="/presets/:presetId" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <div className="App">
            <GoogleAnalytics />
            <AppRoutes />
            <CookiesModal />
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 2500, // Reduced duration
                style: {
                  background: '#1a1a1a',
                  color: '#fff',
                  border: '1px solid #333',
                  cursor: 'pointer',
                  padding: '8px 16px',
                },
              }}
            >
              {(t) => (
                <ToastBar toast={t}>
                  {({ icon, message }) => (
                    <div 
                      onClick={() => toast.dismiss(t.id)} 
                      style={{ display: 'flex', alignItems: 'center', flex: 1 }}
                    >
                      {icon}
                      <div style={{ marginLeft: icon ? '8px' : '0' }}>{message}</div>
                    </div>
                  )}
                </ToastBar>
              )}
            </Toaster>
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
